import { useState, useEffect } from 'react';
import { LogIn, User, Lock, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/core/components/ui/card';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import { useAuthStore } from '@/core/stores/auth-store';
import { appConfig } from '@/core/config';
import { LoadingSpinner } from '@/core/components/LoadingSpinner';
import { environmentConfig, updateApiConstants } from '@/core/api-config';

export function LoginPage() {
  const { login, loginWithToken, isLoading, error, isWaitingForIframeToken, setWaitingForIframeToken } = useAuthStore();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const isProduction = appConfig.isProduction();

  // Dev panel state
  const [devPanelOpen, setDevPanelOpen] = useState(false);
  const [jwtToken, setJwtToken] = useState('');
  const [devEnv, setDevEnv] = useState<'qa' | 'prod'>(
    () => (localStorage.getItem('dev_env_override') as 'qa' | 'prod') ?? 'qa'
  );

  const handleDevEnvChange = (env: 'qa' | 'prod') => {
    setDevEnv(env);
    localStorage.setItem('dev_env_override', env);
    updateApiConstants();
  };

  const handleTokenLogin = async () => {
    const token = jwtToken.replace(/^Bearer\s+/i, '').trim();
    if (!token) return;
    sessionStorage.setItem('cortex_session', token);
    await loginWithToken(token);
  };

  // Get version with fallback
  const { version, isFallback } = appConfig.getVersion();

  // Check if running in iframe
  const isInIframe = window.self !== window.top;

  useEffect(() => {
    // If in iframe, start waiting for token
    if (isInIframe) {
      setWaitingForIframeToken(true);
      
      // After 3 seconds, stop waiting and show login form
      const timeout = setTimeout(() => {
        setWaitingForIframeToken(false);
      }, 3000);
      
      return () => clearTimeout(timeout);
    }
  }, [isInIframe, setWaitingForIframeToken]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Auth store handles isLoading and error internally
    await login({ username, password });
    // Don't navigate - let useAuthInitialization handle routing after login
    // ProtectedRoute will show operator selection if needed
  };

  // Show loader while waiting for iframe token
  if (isWaitingForIframeToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <Card className="shadow-lg border-slate-200/60 bg-white/70 backdrop-blur-sm">
            <CardHeader className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#FFCD00] rounded-full mx-auto">
                <LogIn className="h-6 w-6 text-black" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold text-slate-900">
                  Authenticating...
                </CardTitle>
                <CardDescription className="text-slate-600">
                  Waiting for authentication from parent application
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col items-center py-8">
              <LoadingSpinner text="Please wait" showText />
              
              {/* Version display at bottom */}
              <div className="mt-6 pt-4 border-t border-slate-200 w-full text-center">
                <p className="text-xs text-slate-500" title={isFallback ? 'Using git version (runtime config unavailable)' : 'Backend runtime version'}>
                  Version: {version}{isFallback && ' (fallback)'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Card className="shadow-lg border-slate-200/60 bg-white/70 backdrop-blur-sm">
          <CardHeader className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-[#FFCD00] rounded-full mx-auto">
              <LogIn className="h-6 w-6 text-black" />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold text-slate-900">
                {isProduction ? 'Login' : 'Authentication'}
              </CardTitle>
              <CardDescription className="text-slate-600">
                {isProduction 
                  ? 'Enter your credentials to access the application'
                  : 'Enter credentials to login'
                }
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm font-medium text-slate-700">
                  Username
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter username"
                    className="pl-10 border-slate-200 focus:border-[#FFCD00] focus:ring-[#FFCD00]"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="pl-10 border-slate-200 focus:border-[#FFCD00] focus:ring-[#FFCD00]"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#FFCD00] hover:bg-[#E6B800] text-black py-3 rounded-xl font-semibold transition-colors duration-200"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                    Authenticating...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <LogIn className="h-4 w-4 mr-2" />
                    Login
                  </div>
                )}
              </Button>
            </form>

            {/* Dev Tools Panel - only in non-production */}
            {!isProduction && (
              <div className="mt-4 border border-dashed border-slate-300 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setDevPanelOpen(o => !o)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-slate-500 hover:bg-slate-50 transition-colors"
                >
                  <span>Dev Tools</span>
                  {devPanelOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </button>

                {devPanelOpen && (
                  <div className="px-3 pb-3 space-y-3">
                    {/* Environment switcher */}
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500 font-medium">Environment</p>
                      <div className="flex gap-2">
                        {(['qa', 'prod'] as const).map(env => (
                          <button
                            key={env}
                            type="button"
                            onClick={() => handleDevEnvChange(env)}
                            className={`flex-1 py-1 rounded text-xs font-semibold border transition-colors ${
                              devEnv === env
                                ? 'bg-[#FFCD00] border-[#FFCD00] text-black'
                                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                            }`}
                          >
                            {env.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* JWT token injection */}
                    <div className="space-y-1">
                      <p className="text-xs text-slate-500 font-medium">JWT Token</p>
                      <textarea
                        value={jwtToken}
                        onChange={e => setJwtToken(e.target.value)}
                        placeholder="Paste Bearer token here..."
                        rows={3}
                        className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 resize-none focus:outline-none focus:border-[#FFCD00] font-mono"
                      />
                      <Button
                        type="button"
                        onClick={handleTokenLogin}
                        disabled={!jwtToken.trim() || isLoading}
                        className="w-full h-7 text-xs bg-slate-800 hover:bg-slate-700 text-white"
                      >
                        Use Token
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Version display at bottom of dialog */}
            <div className="mt-6 pt-4 border-t border-slate-200 text-center">
              <p className="text-xs text-slate-500" title={isFallback ? 'Using git version (runtime config unavailable)' : 'Backend runtime version'}>
                Version: {version}{isFallback && ' (fallback)'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
